import { Capacitor, PluginListenerHandle, registerPlugin } from '@capacitor/core'
import { selectAllDrugSearchWords, selectDrugSearchWordIsPresent } from '../dal/ldbDrugSearchWord'
import { selectDrugProductsByQuery } from '../dal/ldbDrugProduct'
import { selectDs1IcdByProductId, selectDs1ManualIndications } from '../dal/ldbDrugIcd'
import { appError, DispatchableFormErrorType } from '../redux/errorSlice'
import { FusionError, serializeFusionError } from '@fusion-advantage/backcompat'
import { AppThunk } from '../redux/appStore'
import { err, ok, Result } from 'neverthrow'

interface SQLitePluginInterface {
	addListener(eventName: string, listenerFunc: (data: any) => void): Promise<PluginListenerHandle>
	returnDataToSwift(data: any): Promise<void>
	returnErrorToSwift(error: { error: string, requestID: string }): Promise<void>
}

const SQLitePlugin = registerPlugin<SQLitePluginInterface>('SQLitePlugin')

let initialized = false
export const initSQLitePlugin = (): AppThunk<Promise<Result<void, FusionError>>> => async (dispatch) => {
	if(initialized) {
		return ok()
	}
	if (Capacitor.isNativePlatform()) {
		try {

			SQLitePlugin.addListener('isDrugNamePresent', async (data) => {
				const { drugName, requestID } = data
				try {
					let r = await selectDrugSearchWordIsPresent(drugName)
					if(r.isErr()) {
						throw r.error
					}
					await SQLitePlugin.returnDataToSwift({ isPresent: r.value, requestID })
				} catch (ex) {
					let err = serializeFusionError('isDrugNamePresent', ex)
					dispatch(appError({ err, type: DispatchableFormErrorType.DbError }))
					await SQLitePlugin.returnErrorToSwift({ error: err.message, requestID })
				}
			})

			SQLitePlugin.addListener('getAllDrugNames', async (data) => {
				const { requestID } = data
				try {
					let r = await selectAllDrugSearchWords()
					if(r.isErr()) {
						throw r.error
					}
					await SQLitePlugin.returnDataToSwift({ drugNames: r.value, requestID })
				} catch (ex) {
					let err = serializeFusionError('getAllDrugNames', ex)
					dispatch(appError({ err, type: DispatchableFormErrorType.DbError }))
					await SQLitePlugin.returnErrorToSwift({ error: err.message, requestID })
				}
			})

			SQLitePlugin.addListener('productSearch', async (data) => {
				const { query, requestID } = data
				try {
					let r = await selectDrugProductsByQuery(query)
					if(r.isErr()) {
						throw r.error
					}
					await SQLitePlugin.returnDataToSwift({ products: r.value, requestID })
				} catch (ex) {
					let err = serializeFusionError('productSearch', ex)
					dispatch(appError({ err, type: DispatchableFormErrorType.DbError }))
					await SQLitePlugin.returnErrorToSwift({ error: err.message, requestID })
				}
			})

			SQLitePlugin.addListener('getIcdList', async (data) => {
				const { productId, requestID } = data
				try {
					let r = await selectDs1IcdByProductId(productId)
					if(r.isErr()) {
						throw r.error
					}
					await SQLitePlugin.returnDataToSwift({ codes: r.value, requestID })
				} catch (ex) {
					let err = serializeFusionError('getIcdList', ex)
					dispatch(appError({ err, type: DispatchableFormErrorType.DbError }))
					await SQLitePlugin.returnErrorToSwift({ error: err.message, requestID })
				}
			})

			SQLitePlugin.addListener('manualIndicationsSearch', async (data) => {
				const { query, type, requestID } = data
				try {
					let r = await selectDs1ManualIndications(query, type)
					if(r.isErr()) {
						throw r.error
					}
					await SQLitePlugin.returnDataToSwift({ codes: r.value, requestID })
				} catch (ex) {
					let err = serializeFusionError('manualIndicationsSearch', ex)
					dispatch(appError({ err, type: DispatchableFormErrorType.DbError }))
					await SQLitePlugin.returnErrorToSwift({ error: err.message, requestID })
				}
			})
		} catch(ex) {
			return err(serializeFusionError('initSQLitePlugin', ex))
		}
	}
	initialized = true
	return ok()
}

export default SQLitePlugin
